import React, { FC, ReactNode, ElementType, MouseEvent } from "react";
import classNames from "classnames";

import Icon from "../Icon";
import { UIStatus } from "../../../types/GlobalTypes";

type AreaProps = {
  type: UIStatus;
  children: ReactNode;
  className?: string;
  noBorder?: boolean;
};

export type MessageProps = {
  img?: ElementType;
  icon?: string;
  heading?: string | ReactNode;
  text: string | ReactNode;
  children?: ReactNode;
  className?: string;
  areaClassName?: string;
  type?: UIStatus;
  onClick?: (e: MouseEvent) => void;
  orientation?: "vertical" | "horizontal";
  noBorder?: boolean;
};

export const MessageArea: FC<AreaProps> = ({
  type = UIStatus.NEUTRAL,
  children,
  className,
  noBorder,
}) => {
  return (
    <div
      className={classNames(
        "rounded-lg p-3 md:p-4",
        {
          border: !noBorder,
          "border-orange-300 bg-orange-50": type === UIStatus.ERROR,
          "border-brown-300 bg-brown-100": type === UIStatus.WARNING,
          "border-brown-300 bg-brown-50": type === UIStatus.ATTENTION,
          "border-green-300 bg-green-50": type === UIStatus.SUCCESS,
          "border-slate-400 bg-slate-100": type === UIStatus.NEUTRAL,
        },
        className
      )}
    >
      {children}
    </div>
  );
};

export const Message: FC<MessageProps> = (props) => {
  const {
    img: Image,
    icon,
    heading,
    text,
    type = UIStatus.NEUTRAL,
    children,
    className,
    areaClassName,
    onClick,
    orientation = "vertical",
    noBorder,
  } = props;

  const contentOrientationClass =
    orientation === "vertical"
      ? "flex-col gap-y-3"
      : "flex-row gap-x-3 items-center";

  return (
    <div className={className} onClick={onClick}>
      <MessageArea type={type} className={areaClassName} noBorder={noBorder}>
        <div className="flex flex-row items-center gap-x-6">
          {Image && <Image className="" height={96} width={96} />}

          <div className={`flex flex-1 shrink-1 ${contentOrientationClass}`}>
            {icon && (
              <Icon
                icon={icon}
                size={32}
                className="text-gray-500"
                disableFill
              />
            )}

            <div className="flex flex-1 shrink-1 flex-col gap-y-1">
              {heading && (
                <div className="text-sm text-gray-500 font-manrope font-bold">
                  {heading}
                </div>
              )}

              {text && (
                <div className="text-sm text-gray-500 font-manrope font-semibold">
                  {text}
                </div>
              )}
            </div>

            {children && (
              <div className={orientation === "vertical" ? "mt-3" : ""}>
                {children}
              </div>
            )}
          </div>
        </div>
      </MessageArea>
    </div>
  );
};
